import React, { useEffect, useRef, useState } from "react";
import {
  Modal, Progress, Tag, Button, Alert, Space,
  Typography, Radio, Checkbox,
} from "antd";
import { SbInput } from "uiCore";
import {
  MinusOutlined, CloseOutlined, DownloadOutlined,
  ReloadOutlined, RobotOutlined, ClockCircleOutlined,
} from "@ant-design/icons";
import { EventSourcePolyfill } from "event-source-polyfill";
import useAuthStore from "Store/useAuthStore";
import { submitAgentBuildAnswer, getAgentBuildDownloadUrl } from "Api/apiService";
import { API_ENDPOINTS } from "config/ApiConfig";

const { Text, Title } = Typography;

// Ordered pipeline stages — matched against actual log lines emitted by the Cloud Run job.
// The 22–98% range is filled by a slow fake increment (see useEffect below) since AI pipeline
// steps are not individually streamed. Capped at 98 so 100% is only ever set by a real event.
const PIPELINE_STAGES = [
  { pct:  5, label: "Fetching PRD…",        match: /Step 1\/4/i },
  { pct: 15, label: "Preparing workspace…", match: /Step 2\/4/i },
  { pct: 22, label: "AI pipeline running…", match: /Step 3\/4/i },
  { pct: 92, label: "Packaging…",           match: /Step 4\/4/i },
  { pct:100, label: "Agent built successfully", match: /uploaded successfully|package uploaded/i },
];

function detectProgress(line) {
  let result = null;
  for (const stage of PIPELINE_STAGES) {
    if (stage.match.test(line)) result = stage;
  }
  return result;
}

const COLD_START_MESSAGES = [
  "Starting…",
  "Spinning up your cloud environment…",
  "Warming up the pipeline…",
  "Cloud VM coming online…",
  "Preparing the build workspace…",
  "Loading AI models into memory…",
];

const BUILD_PROGRESS_KEY = (buildId) => `sb_agent_build_pct_${buildId}`;

function restoreProgress(buildId) {
  if (!buildId) return null;
  const saved = localStorage.getItem(BUILD_PROGRESS_KEY(buildId));
  if (!saved) return null;
  const pct = parseInt(saved, 10);
  if (isNaN(pct) || pct <= 0 || pct >= 100) return null;
  let label = COLD_START_MESSAGES[0];
  for (const stage of PIPELINE_STAGES) {
    if (stage.pct <= pct) label = stage.label;
  }
  return { pct, label };
}

/**
 * AgentBuilderPanel
 *
 * Shows real-time pipeline progress via SSE and handles interactive Q&A.
 * Mount this when a build is in progress or complete (buildId is set).
 *
 * Props:
 *   buildId       string | null  — active build ID
 *   buildStatus   string | null  — current status from parent
 *   onComplete    (gcsPath) => void  — called on DONE
 *   onError       (status, message) => void  — called on FAILED | TIMED_OUT
 *   onClose       () => void  — fully dismiss the panel
 *   onMinimize    () => void | undefined  — called when user clicks −
 *   onRebuild     () => void | undefined  — only passed for FAILED / TIMED_OUT
 */
const AgentBuilderPanel = ({ buildId, buildStatus, onComplete, onError, onClose, onMinimize, onRebuild }) => {
  const accessToken = useAuthStore((s) => s.accessToken);

  const [question, setQuestion]         = useState(null);
  const [answer, setAnswer]             = useState("");
  const [submitting, setSubmitting]     = useState(false);
  const [finished, setFinished]         = useState(false);
  const [errorStatus, setErrorStatus]   = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  const [downloadUrl, setDownloadUrl]   = useState(null);
  const [timeLeft, setTimeLeft]         = useState(null);
  const [progress, setProgress]         = useState(() => restoreProgress(buildId) || { pct: 0, label: "Starting…" });
  const [isMinimized, setIsMinimized]   = useState(false);
  const [tokenUsage, setTokenUsage]     = useState(null);

  const esRef        = useRef(null);
  const timerRef     = useRef(null);
  const coldStartRef = useRef(null); // { startedAt: number, elapsed: number }

  const isAlreadyBuilt = buildStatus === "DONE" && !finished;
  const isTerminal     = finished || isAlreadyBuilt;

  // SSE connection
  useEffect(() => {
    if (!buildId || !accessToken || isAlreadyBuilt) return;

    const url = `${API_ENDPOINTS.AGENT_BUILDER_STREAM}${buildId}`;
    const es  = new EventSourcePolyfill(url, {
      headers: { Authorization: `Bearer ${accessToken}` },
      heartbeatTimeout: 4 * 60 * 60 * 1000,
    });
    esRef.current = es;

    es.addEventListener("log_line", (e) => {
      try {
        const data  = JSON.parse(e.data);
        const line  = data.line || e.data;
        const stage = detectProgress(line);
        if (stage) setProgress((prev) => stage.pct > prev.pct ? { pct: stage.pct, label: stage.label } : prev);
      } catch { /* ignore */ }
    });

    es.addEventListener("question", (e) => {
      try {
        const data = JSON.parse(e.data);
        setQuestion(data);
        setAnswer("");
        setTimeLeft(30 * 60);
      } catch { /* ignore */ }
    });

    es.addEventListener("completion", (e) => {
      try {
        const data  = JSON.parse(e.data);
        const usage = data.token_usage || null;
        setFinished(true);
        setProgress({ pct: 100, label: "Agent built successfully" });
        localStorage.removeItem(BUILD_PROGRESS_KEY(buildId));
        if (usage) setTokenUsage(usage);
        es.close();
        esRef.current = null;
        clearInterval(timerRef.current);
        if (onComplete) onComplete(data.gcs_path, usage);
        getAgentBuildDownloadUrl(buildId)
          .then((res) => setDownloadUrl(res.download_url))
          .catch(() => {});
      } catch { /* ignore */ }
    });

    es.addEventListener("error", (e) => {
      try {
        const data    = JSON.parse(e.data);
        const status  = data.status  || "FAILED";
        const message = data.message || "Build failed — please try again.";
        setFinished(true);
        setProgress((prev) => ({ pct: prev.pct, label: status === "TIMED_OUT" ? "Timed out" : "Failed" }));
        localStorage.removeItem(BUILD_PROGRESS_KEY(buildId));
        setErrorStatus(status);
        setErrorMessage(message);
        es.close();
        esRef.current = null;
        clearInterval(timerRef.current);
        if (onError) onError(status, message);
      } catch { /* ignore */ }
    });

    es.onerror = () => {};

    return () => { es.close(); esRef.current = null; };
  }, [buildId, accessToken]);

  // Countdown timer for Q&A
  useEffect(() => {
    if (timeLeft === null) return;
    if (timeLeft <= 0) { setQuestion(null); setTimeLeft(null); return; }
    timerRef.current = setInterval(() => {
      setTimeLeft((t) => (t !== null ? t - 1 : null));
    }, 1000);
    return () => clearInterval(timerRef.current);
  }, [timeLeft !== null && timeLeft > 0 ? "running" : "stopped"]);

  // Persist progress to localStorage
  useEffect(() => {
    if (!buildId || isAlreadyBuilt || progress.pct <= 0) return;
    localStorage.setItem(BUILD_PROGRESS_KEY(buildId), String(progress.pct));
  }, [progress.pct, buildId, isAlreadyBuilt]);

  // Cold-start message sequence: play each message once, then show elapsed time counter.
  // Never loops — once all messages are shown the label becomes "VM still initializing… Xm Ys".
  useEffect(() => {
    if (progress.pct > 0 || finished || !buildId) return;
    coldStartRef.current = { startedAt: Date.now() };
    let idx = 0;
    const id = setInterval(() => {
      idx += 1;
      if (idx < COLD_START_MESSAGES.length) {
        setProgress((prev) => prev.pct === 0 ? { pct: 0, label: COLD_START_MESSAGES[idx] } : prev);
      } else {
        setProgress((prev) => prev.pct === 0 ? { pct: 0, label: "VM still initializing…" } : prev);
      }
    }, 15000);
    return () => clearInterval(id);
  }, [progress.pct, finished, buildId]);

  // Slow fake increment 22–98%
  useEffect(() => {
    if (finished) return;
    const id = setInterval(() => {
      setProgress((prev) => {
        if (prev.pct >= 15 && prev.pct < 92) return { ...prev, pct: prev.pct + 1 };
        return prev;
      });
    }, 9000);
    return () => clearInterval(id);
  }, [finished, buildId]);

  const formatCountdown = (secs) => {
    if (secs === null) return "";
    const m = String(Math.floor(secs / 60)).padStart(2, "0");
    const s = String(secs % 60).padStart(2, "0");
    return `${m}:${s}`;
  };

  const handleSubmitAnswer = async () => {
    if (!answer.trim()) return;
    setSubmitting(true);
    try {
      await submitAgentBuildAnswer(buildId, answer.trim());
      setQuestion(null);
      setTimeLeft(null);
      clearInterval(timerRef.current);
    } catch (err) {
      console.error("Failed to submit answer:", err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDownload = () => {
    if (!downloadUrl) return;
    const a = document.createElement("a");
    a.href = downloadUrl;
    a.download = "";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  // Progress bar derived values
  const pct           = isAlreadyBuilt ? 100 : progress.pct;
  const progressLabel = isAlreadyBuilt ? "Agent built successfully" : progress.label;
  const progressStatus = (finished && errorStatus) ? "exception"
                       : pct === 100               ? "success"
                       :                             "active";
  const strokeColor   = (finished && errorStatus === "TIMED_OUT") ? "#FAAD14"
                      : (finished && errorStatus)                  ? "#ff4d4f"
                      : pct === 100                                ? "#52c41a"
                      :                                             "#1677ff";

  // Status tag
  const statusTag = !isTerminal
    ? <Tag color="processing">Running…</Tag>
    : errorStatus === "TIMED_OUT" ? <Tag color="warning">Timed Out</Tag>
    : errorStatus                 ? <Tag color="error">Failed</Tag>
    :                               <Tag color="success">Done</Tag>;

  // Modal title row
  const modalTitle = (
    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
      <RobotOutlined style={{ fontSize: 16, color: "#1677ff" }} />
      <Title level={5} style={{ margin: 0, fontSize: 15 }}>Agent Builder</Title>
      {statusTag}
      <div style={{ marginLeft: "auto", display: "flex", gap: 4 }}>
        {!isTerminal && (
          <Button
            type="text"
            size="small"
            icon={<MinusOutlined />}
            onClick={() => { if (onMinimize) onMinimize(); else setIsMinimized(true); }}
            title="Minimize"
          />
        )}
        {isTerminal && onClose && (
          <Button type="text" size="small" icon={<CloseOutlined />} onClick={onClose} title="Close" />
        )}
      </div>
    </div>
  );

  // ── Minimized pill ──────────────────────────────────────────────────────────
  if (isMinimized) {
    const pillLabel = finished && !errorStatus           ? "Agent Built"
                    : finished && errorStatus === "TIMED_OUT" ? "Build Timed Out"
                    : finished && errorStatus            ? "Build Failed"
                    :                                     `Building… ${progress.pct}%`;
    const pillColor = finished && !errorStatus ? "#52c41a"
                    : finished && errorStatus  ? "#ff4d4f"
                    :                           "#1677ff";
    return (
      <div
        onClick={() => setIsMinimized(false)}
        style={{
          position: "fixed", bottom: 24, right: 24, zIndex: 1200,
          display: "flex", alignItems: "center", gap: 8,
          background: "#fff", borderRadius: 24,
          border: `2px solid ${pillColor}`,
          padding: "8px 14px",
          boxShadow: "0 4px 16px rgba(0,0,0,0.18)",
          cursor: "pointer",
        }}
      >
        <span style={{ width: 8, height: 8, borderRadius: "50%", background: pillColor, flexShrink: 0 }} />
        <Text strong style={{ fontSize: 13, whiteSpace: "nowrap" }}>{pillLabel}</Text>
        {onClose && (
          <Button
            type="text"
            size="small"
            icon={<CloseOutlined />}
            onClick={(e) => { e.stopPropagation(); onClose(); }}
          />
        )}
      </div>
    );
  }

  // ── Full panel ──────────────────────────────────────────────────────────────
  return (
    <Modal
      open
      centered
      width={680}
      footer={null}
      closable={false}
      maskClosable={false}
      title={modalTitle}
      styles={{ body: { padding: "20px 24px", display: "flex", flexDirection: "column", gap: 20 } }}
    >
      {/* Progress */}
      <div>
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
          <Text type="secondary" style={{ fontSize: 12 }}>{progressLabel}</Text>
          <Text type="secondary" style={{ fontSize: 12 }}>{pct}%</Text>
        </div>
        <Progress
          percent={pct}
          status={progressStatus}
          strokeColor={strokeColor}
          showInfo={false}
          strokeWidth={8}
        />
      </div>

      {/* Q&A dialog */}
      {question && !finished && (
        <div style={{ background: "#fffbe6", border: "1px solid #ffe58f", borderRadius: 8, padding: 16 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
            <Text strong>{question.header || "Question"}</Text>
            {timeLeft !== null && (
              <Text type={timeLeft < 300 ? "danger" : "secondary"} style={{ fontSize: 12 }}>
                <ClockCircleOutlined style={{ marginRight: 4 }} />
                {formatCountdown(timeLeft)}
              </Text>
            )}
          </div>
          <Text style={{ display: "block", marginBottom: 12 }}>{question.question}</Text>

          {question.options?.length > 0 && (
            <div style={{ marginBottom: 12 }}>
              {question.multi_select ? (
                <Checkbox.Group
                  options={question.options}
                  value={answer ? answer.split(",") : []}
                  onChange={(vals) => setAnswer(vals.join(","))}
                  style={{ display: "flex", flexDirection: "column", gap: 6 }}
                />
              ) : (
                <Radio.Group
                  options={question.options}
                  value={answer}
                  onChange={(e) => setAnswer(e.target.value)}
                  style={{ display: "flex", flexDirection: "column", gap: 6 }}
                />
              )}
            </div>
          )}

          <SbInput
            textarea
            rows={2}
            placeholder={question.options?.length ? "Or type a custom answer…" : "Your answer…"}
            value={question.options?.length ? "" : answer}
            onChange={(e) => { if (!question.options?.length) setAnswer(e.target.value); }}
            style={{ marginBottom: 12 }}
          />

          <Button
            type="primary"
            loading={submitting}
            disabled={!answer.trim()}
            onClick={handleSubmitAnswer}
          >
            Submit Answer
          </Button>
        </div>
      )}

      {/* Error banner */}
      {finished && errorStatus && (
        <Alert
          type={errorStatus === "TIMED_OUT" ? "warning" : "error"}
          message={errorStatus === "TIMED_OUT" ? "Build timed out" : "Build failed"}
          description={errorMessage}
          showIcon
        />
      )}

      {/* Footer actions */}
      {isTerminal && (
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          {isAlreadyBuilt
            ? <Text type="secondary" style={{ fontSize: 13 }}>This agent was previously built.</Text>
            : <span />
          }
          <Space>
            {onRebuild && (
              <Button danger icon={<ReloadOutlined />} onClick={onRebuild}>
                Retry Build
              </Button>
            )}
            {downloadUrl && (
              <Button type="primary" icon={<DownloadOutlined />} onClick={handleDownload}>
                Download Agent
              </Button>
            )}
          </Space>
        </div>
      )}
    </Modal>
  );
};

export default AgentBuilderPanel;
